import { addDays, endOfMonth, endOfWeek, endOfYear, format, isWithinInterval, parseISO, startOfDay, startOfMonth, startOfWeek, startOfYear } from "date-fns";

export const LACW_CALENDAR_FORM_TYPE = "lacw_calendar_event";
export const CALENDAR_VIEWS = ["day", "week", "fortnight", "month", "year"];

export function getCalendarRange(anchor, view) {
  const date = startOfDay(anchor);
  if (view === "day") return { start: date, end: date };
  if (view === "week") {
    const start = startOfWeek(date, { weekStartsOn: 1 });
    return { start, end: endOfWeek(date, { weekStartsOn: 1 }) };
  }
  if (view === "fortnight") {
    const start = startOfWeek(date, { weekStartsOn: 1 });
    return { start, end: addDays(start, 13) };
  }
  if (view === "year") return { start: startOfYear(date), end: endOfYear(date) };
  return { start: startOfMonth(date), end: endOfMonth(date) };
}

export function shiftCalendarAnchor(anchor, view, direction) {
  const amount = view === "day" ? 1 : view === "week" ? 7 : view === "fortnight" ? 14 : view === "year" ? 365 : 32;
  const shifted = addDays(anchor, amount * direction);
  if (view === "month") return new Date(shifted.getFullYear(), shifted.getMonth(), 1);
  if (view === "year") return new Date(anchor.getFullYear() + direction, 0, 1);
  return shifted;
}

export function calendarRangeLabel(anchor, view) {
  const { start, end } = getCalendarRange(anchor, view);
  if (view === "day") return format(start, "EEEE d MMMM yyyy");
  if (view === "month") return format(start, "MMMM yyyy");
  if (view === "year") return format(start, "yyyy");
  return `${format(start, "d MMM")} – ${format(end, "d MMM yyyy")}`;
}

export function eventOccursInRange(event, range) {
  if (!event?.date) return false;
  const date = parseISO(event.date);
  return !Number.isNaN(date.getTime()) && isWithinInterval(date, range);
}

export function normalizeCalendarDraft(draft) {
  const data = draft?.form_data || {};
  return { ...data, id: draft.id, updatedAt: draft.updated_at, updatedBy: draft.saved_by };
}

export function buildCalendarDraft(event) {
  return {
    id: event.id,
    form_type: LACW_CALENDAR_FORM_TYPE,
    brand: "lacw",
    draft_name: `${event.date} - ${event.clientName || event.matterNumber || "LACW matter"}`,
    form_data: {
      date: event.date,
      startTime: event.startTime || "09:00",
      endTime: event.endTime || "10:00",
      clientName: event.clientName?.trim() || "",
      matterNumber: event.matterNumber?.trim() || "",
      court: event.court?.trim() || "",
      listingType: event.listingType?.trim() || "",
      responsibleStaff: event.responsibleStaff?.trim() || "",
      appearing: event.appearing?.trim() || "TBC",
      billable: Boolean(event.billable),
      billingStatus: event.billable ? (event.billingStatus || "To be billed") : "Not marked",
      notes: event.notes?.trim() || "",
      microsoftCalendarId: event.microsoftCalendarId || "",
      microsoftEventId: event.microsoftEventId || "",
      microsoftCalendarName: event.microsoftCalendarName || "",
    },
  };
}

export function buildMicrosoftCalendarEvent(event) {
  const matterLabel = event.matterNumber ? ` (${event.matterNumber})` : "";
  return {
    subject: `LACW – ${event.clientName}${matterLabel}`,
    start: { dateTime: `${event.date}T${event.startTime || "09:00"}:00`, timeZone: "Australia/Sydney" },
    end: { dateTime: `${event.date}T${event.endTime || "10:00"}:00`, timeZone: "Australia/Sydney" },
    location: { displayName: event.court || "Location TBC" },
    body: {
      contentType: "text",
      content: ["LACW Staff Calendar", `Client: ${event.clientName}`, `Matter: ${event.matterNumber || "Not entered"}`, `Listing: ${event.listingType || "Not entered"}`, `Responsible staff: ${event.responsibleStaff}`, `Appearing: ${event.appearing || "TBC"}`, `Billing: ${event.billable ? "To be billed" : "Not marked"}`, event.notes ? `Notes: ${event.notes}` : ""].filter(Boolean).join("\n"),
    },
    categories: ["LACW"],
  };
}

export function isPrivateMicrosoftEvent(event) {
  return String(event?.sensitivity || "normal").toLowerCase() === "private";
}
